import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack,
  Switch, TextField, Typography,
} from '@mui/material'
import { Add as AddIcon, ArrowBack as ArrowBackIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useCompany, useCompanyStatus, useUpdateCompany } from '../../hooks/api/useCompanies'
import { useToast } from '../../contexts/ToastContext'
import { CompanyDetail, DiscountSlab, UpdateCompanyPayload } from '../../types/company'
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext'

const defaultHeaderColor = '#2d3a4a'
const isHexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value)

const toForm = (company: CompanyDetail): UpdateCompanyPayload => ({
  name: company.name,
  email: company.email,
  phone: company.phone,
  address: company.address,
  website: company.website || '',
  industry: company.industry || '',
  features: {
    sales_tax_invoice: company.features.sales_tax_invoice,
    advance_tax: company.features.advance_tax,
    carton_discount_pricing: company.features.carton_discount_pricing,
    loose_pcs: company.features.loose_pcs,
  },
  unit_of_measure: {
    code: company.unit_of_measure.code,
    singular: company.unit_of_measure.singular,
    plural: company.unit_of_measure.plural,
    allows_decimal: company.unit_of_measure.allows_decimal,
  },
  branding: { header_color: company.branding?.header_color || defaultHeaderColor },
  billing_config: {
    discount_type: company.billing_config.discount_type,
    show_discount: company.billing_config.show_discount,
    wholesale_discount_percentage: company.billing_config.wholesale_discount_percentage,
    carton_discount_slabs: company.billing_config.carton_discount_slabs.map((slab) => ({
      min_ctn: slab.min_ctn,
      max_ctn: slab.max_ctn,
      discount_percentage: slab.discount_percentage,
    })),
  },
})

const validateForm = (form: UpdateCompanyPayload): string | null => {
  if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.address.trim())
    return 'Complete all required company profile fields.'
  if (!form.unit_of_measure.code.trim() || !form.unit_of_measure.singular.trim() || !form.unit_of_measure.plural.trim())
    return 'Complete all unit of measure fields.'
  if (!isHexColor(form.branding.header_color))
    return 'Header color must be a 6-digit hex value, such as #34495E.'
  if (form.billing_config.wholesale_discount_percentage < 0 || form.billing_config.wholesale_discount_percentage > 100)
    return 'Wholesale discount must be between 0% and 100%.'
  const slabs = form.billing_config.carton_discount_slabs
  if (slabs.length === 0) return 'Add at least one discount slab.'
  let expectedMin = 1
  for (let index = 0; index < slabs.length; index += 1) {
    const slab = slabs[index]
    if (!Number.isInteger(slab.min_ctn) || slab.min_ctn !== expectedMin)
      return 'Discount slabs must start at 1 and continue without gaps or overlaps.'
    if (slab.discount_percentage < 0 || slab.discount_percentage > 100)
      return 'Every slab discount must be between 0% and 100%.'
    if (slab.max_ctn === null) {
      if (index !== slabs.length - 1) return 'Only the final slab can be open-ended.'
    } else {
      if (!Number.isInteger(slab.max_ctn) || slab.max_ctn < slab.min_ctn)
        return 'Each slab maximum must be at least its minimum.'
      expectedMin = slab.max_ctn + 1
    }
  }
  if (slabs[slabs.length - 1].max_ctn !== null) return 'The final slab must have no maximum.'
  return null
}

const CompanyDetailPage = () => {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { data: company, isLoading, error } = useCompany(id)
  const updateCompany = useUpdateCompany(id)
  const updateStatus = useCompanyStatus(id)
  const [form, setForm] = useState<UpdateCompanyPayload | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const { setUnsavedChanges, confirmLeave } = useUnsavedChanges()

  useEffect(() => {
    if (company && !dirty) setForm(toForm(company))
  }, [company, dirty])
  useEffect(() => {
    setUnsavedChanges(dirty)
    return () => setUnsavedChanges(false)
  }, [dirty, setUnsavedChanges])

  const setProfile = (key: 'name' | 'email' | 'phone' | 'address' | 'website' | 'industry', value: string) => {
    setForm((current) => current && ({ ...current, [key]: value }))
    setDirty(true)
  }
  const setFeature = (key: keyof UpdateCompanyPayload['features'], value: boolean) => {
    setForm((current) => current && ({ ...current, features: { ...current.features, [key]: value } }))
    setDirty(true)
  }
  const setUom = (key: keyof UpdateCompanyPayload['unit_of_measure'], value: string | boolean) => {
    setForm((current) => current && ({ ...current, unit_of_measure: { ...current.unit_of_measure, [key]: value } }))
    setDirty(true)
  }
  const setHeaderColor = (value: string) => {
    setForm((current) => current && ({ ...current, branding: { ...current.branding, header_color: value } }))
    setDirty(true)
  }
  const setBilling = (key: 'discount_type' | 'show_discount' | 'wholesale_discount_percentage', value: string | boolean | number) => {
    setForm((current) => current && ({ ...current, billing_config: { ...current.billing_config, [key]: value } }))
    setDirty(true)
  }
  const setSlabs = (slabs: DiscountSlab[]) => {
    setForm((current) => current && ({ ...current, billing_config: { ...current.billing_config, carton_discount_slabs: slabs } }))
    setDirty(true)
  }
  const updateSlab = (index: number, key: keyof DiscountSlab, value: number | null) => {
    if (!form) return
    setSlabs(form.billing_config.carton_discount_slabs.map((slab, itemIndex) => itemIndex === index ? { ...slab, [key]: value } : slab))
  }
  const addSlab = () => {
    if (!form) return
    const slabs = [...form.billing_config.carton_discount_slabs]
    const last = slabs[slabs.length - 1]
    const start = last ? (last.max_ctn ?? last.min_ctn) + 1 : 1
    if (last?.max_ctn === null) slabs[slabs.length - 1] = { ...last, max_ctn: start - 1 }
    setSlabs([...slabs, { min_ctn: start, max_ctn: null, discount_percentage: 0 }])
  }
  const removeSlab = (index: number) => {
    if (!form || form.billing_config.carton_discount_slabs.length === 1) return
    const slabs = form.billing_config.carton_discount_slabs.filter((_, itemIndex) => itemIndex !== index)
    if (index === form.billing_config.carton_discount_slabs.length - 1)
      slabs[slabs.length - 1] = { ...slabs[slabs.length - 1], max_ctn: null }
    setSlabs(slabs)
  }
  const goBack = () => {
    if (confirmLeave()) navigate('/companies')
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!form) return
    const validationError = validateForm(form)
    if (validationError) { setSaveError(validationError); return }
    setSaveError('')
    try {
      await updateCompany.mutateAsync(form)
      setDirty(false)
      showToast('Company settings saved', 'success')
    } catch (reason) {
      setSaveError((reason as Error).message || 'Could not save company settings.')
    }
  }
  const nextStatus = company?.status === 'active' ? 'suspended' : 'active'
  const changeStatus = async () => {
    try {
      await updateStatus.mutateAsync(nextStatus)
      setStatusDialogOpen(false)
      showToast(nextStatus === 'active' ? 'Company activated' : 'Company suspended', 'success')
    } catch (reason) {
      setSaveError((reason as Error).message || 'Could not change company status.')
      setStatusDialogOpen(false)
    }
  }

  if (isLoading) return <LoadingSpinner message="Loading company..." />
  if (error || !company || !form) return <Alert severity="error">{(error as Error)?.message || 'Company not found.'}</Alert>

  const info = company.sales_tax_info || {}
  const stiReady = Boolean(info.supplier_name && info.address && info.strn && info.ntn && info.invoice_fixed_code && info.sti_counter_start)
  const statusAction = nextStatus === 'suspended' ? 'Suspend company' : 'Activate company'

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>Companies</Button>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h4" fontWeight={700}>{company.name}</Typography>
            <Chip size="small" label={company.status === 'active' ? 'active' : 'suspended'} color={company.status === 'active' ? 'success' : 'warning'} />
          </Stack>
          <Typography color="text.secondary">Company ID: {company._id}</Typography>
        </Box>
        <Button variant="outlined" color={nextStatus === 'suspended' ? 'warning' : 'success'} disabled={dirty} onClick={() => setStatusDialogOpen(true)}>
          {statusAction}
        </Button>
      </Stack>
      {dirty && <Alert severity="info" sx={{ mb: 2 }}>You have unsaved changes. Save them before changing company status.</Alert>}
      <Box component="form" onSubmit={save}>
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={2}>Company profile</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Name" required value={form.name} onChange={(event) => setProfile('name', event.target.value)} inputProps={{ minLength: 2, maxLength: 100 }} />
            <TextField label="Email" type="email" required value={form.email} onChange={(event) => setProfile('email', event.target.value)} />
            <TextField label="Phone" required value={form.phone} onChange={(event) => setProfile('phone', event.target.value)} inputProps={{ minLength: 7, maxLength: 20 }} />
            <TextField label="Address" required value={form.address} onChange={(event) => setProfile('address', event.target.value)} />
            <TextField label="Website" value={form.website} onChange={(event) => setProfile('website', event.target.value)} />
            <TextField label="Industry" value={form.industry} onChange={(event) => setProfile('industry', event.target.value)} />
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={0.5}>Branding</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>Used for the company header and sidebar accents.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Pick color"
              type="color"
              value={isHexColor(form.branding.header_color) ? form.branding.header_color : defaultHeaderColor}
              onChange={(event) => setHeaderColor(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 120 }}
            />
            <TextField
              label="Header color (hex)"
              value={form.branding.header_color}
              onChange={(event) => setHeaderColor(event.target.value)}
              inputProps={{ maxLength: 7 }}
              error={!isHexColor(form.branding.header_color)}
              helperText="Use #RRGGBB"
              sx={{ width: 220 }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={2}>Features</Typography>
          <Stack spacing={1}>
            <FormControlLabel control={<Switch checked={form.features.sales_tax_invoice} onChange={(event) => setFeature('sales_tax_invoice', event.target.checked)} disabled={!stiReady && !form.features.sales_tax_invoice} />} label="Sales Tax Invoice (STI)" />
            {!stiReady && !form.features.sales_tax_invoice && <Typography variant="body2" color="text.secondary">STI supplier and numbering details must be provisioned before this can be enabled.</Typography>}
            <FormControlLabel control={<Switch checked={form.features.advance_tax} onChange={(event) => setFeature('advance_tax', event.target.checked)} />} label="Advance tax" />
            <FormControlLabel control={<Switch checked={form.features.carton_discount_pricing} onChange={(event) => setFeature('carton_discount_pricing', event.target.checked)} />} label="Carton discount pricing" />
            <FormControlLabel control={<Switch checked={form.features.loose_pcs} onChange={(event) => setFeature('loose_pcs', event.target.checked)} />} label="Loose pieces in billing" />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6">Unit of measure</Typography>
          {!company.uom_editable && <Alert severity="info" sx={{ my: 2 }}>This company already has bills, so its unit of measure is locked.</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mt: 2 }}>
            <TextField label="Code" value={form.unit_of_measure.code} onChange={(event) => setUom('code', event.target.value.toUpperCase())} disabled={!company.uom_editable} inputProps={{ maxLength: 12 }} />
            <TextField label="Singular" value={form.unit_of_measure.singular} onChange={(event) => setUom('singular', event.target.value)} disabled={!company.uom_editable} inputProps={{ maxLength: 30 }} />
            <TextField label="Plural" value={form.unit_of_measure.plural} onChange={(event) => setUom('plural', event.target.value)} disabled={!company.uom_editable} inputProps={{ maxLength: 30 }} />
          </Box>
          <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={form.unit_of_measure.allows_decimal} onChange={(event) => setUom('allows_decimal', event.target.checked)} disabled={!company.uom_editable} />} label="Allow decimal quantities" />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={2}>Billing settings</Typography>
          <Stack spacing={2}>
            <TextField select label="Discount type" value={form.billing_config.discount_type} onChange={(event) => setBilling('discount_type', event.target.value)} sx={{ maxWidth: 280 }}>
              <MenuItem value="per_sku">Per SKU</MenuItem>
              <MenuItem value="whole_bill">Whole bill</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch checked={form.billing_config.show_discount} onChange={(event) => setBilling('show_discount', event.target.checked)} />} label="Show discount on standard invoices" />
            <TextField label="Wholesale discount (%)" type="number" value={form.billing_config.wholesale_discount_percentage} onChange={(event) => setBilling('wholesale_discount_percentage', Number(event.target.value))} inputProps={{ min: 0, max: 100, step: 0.01 }} sx={{ maxWidth: 280 }} />
          </Stack>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Retail discount slabs</Typography>
              <Typography variant="body2" color="text.secondary">Used when carton discount pricing is enabled.</Typography>
            </Box>
            <Button startIcon={<AddIcon />} onClick={addSlab}>Add slab</Button>
          </Stack>
          <Stack spacing={1.5}>
            {form.billing_config.carton_discount_slabs.map((slab, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField label="From" type="number" size="small" value={slab.min_ctn} onChange={(event) => updateSlab(index, 'min_ctn', Number(event.target.value))} inputProps={{ min: 1, step: 1 }} />
                <TextField label="To" type="number" size="small" value={slab.max_ctn ?? ''} onChange={(event) => updateSlab(index, 'max_ctn', event.target.value === '' ? null : Number(event.target.value))} helperText={index === form.billing_config.carton_discount_slabs.length - 1 ? 'Blank = no limit' : undefined} inputProps={{ min: 1, step: 1 }} />
                <TextField label="Discount %" type="number" size="small" value={slab.discount_percentage} onChange={(event) => updateSlab(index, 'discount_percentage', Number(event.target.value))} inputProps={{ min: 0, max: 100, step: 0.01 }} />
                <IconButton aria-label={`Remove slab ${index + 1}`} onClick={() => removeSlab(index)} disabled={form.billing_config.carton_discount_slabs.length === 1}><DeleteIcon /></IconButton>
              </Stack>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={0.5}>Provisioned details</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>These details are read-only in Company Management.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {[
              ['Template code', company.template_code],
              ['STI supplier', info.supplier_name],
              ['STI address', info.address],
              ['STRN', info.strn],
              ['NTN', info.ntn],
              ['Contact', info.contact],
              ['Invoice fixed code', info.invoice_fixed_code],
              ['STI counter start', info.sti_counter_start],
            ].map(([label, value]) => (
              <Box key={String(label)}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2">{value ?? 'Not set'}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Stack direction="row" justifyContent="flex-end" spacing={1} mb={4}>
          <Button onClick={goBack}>Back</Button>
          <Button type="submit" variant="contained" disabled={!dirty || updateCompany.isPending}>
            {updateCompany.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </Stack>
      </Box>

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>{statusAction}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {nextStatus === 'suspended'
              ? `Users of ${company.name} will be blocked from logging in and using existing sessions.`
              : `Users of ${company.name} will be able to access the app again.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={updateStatus.isPending}>Cancel</Button>
          <Button onClick={changeStatus} color={nextStatus === 'suspended' ? 'warning' : 'success'} variant="contained" disabled={updateStatus.isPending}>
            {updateStatus.isPending ? 'Updating...' : statusAction}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CompanyDetailPage
